import { useState } from "react";
import QRCode from "qrcode";
import host from "../global";
import { candidateAuthHeader } from "../utils/candidateAuth";
import { QR_CODE_OPTIONS } from "../constants/secondaryCamera";

export const checkSecondaryCameraLive = async (interviewId, round) => {
  if (!interviewId || !round) {
    return false;
  }
  try {
    const response = await fetch(
      `${host}/secondary-camera/pairing-status/${interviewId}?round=${round}`,
      { headers: candidateAuthHeader(interviewId) }
    );
    if (!response.ok) return false;
    const data = await response.json();
    return Boolean(data.live);
  } catch {
    return false;
  }
};

// Owns the pairing QR for one round's secondary (phone) camera. The QR encodes a
// one-time session URL; the phone claims it and streams periodic screenshots.
// Pairing is verified once, on the Continue click (no polling).
export const useSecondaryCamera = ({ interviewId, isSecondaryCameraEnabled, round }) => {
  const [qrCode, setQrCode] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState(null);
  const [qrThrottled, setQrThrottled] = useState(false);

  // force=true regenerates even over a live pairing (the Regenerate button); a
  // plain call is non-destructive and yields `alreadyPaired` when a phone is on.
  const generateSecondaryQR = async (force = false) => {
    if (!isSecondaryCameraEnabled) {
      return;
    }
    setQrLoading(true);
    setQrError(null);
    setQrThrottled(false);

    try {
      const response = await fetch(`${host}/secondary-camera/sessions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...candidateAuthHeader(interviewId) },
        body: JSON.stringify({ interview_id: interviewId, round, force }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Server error (${response.status})`);
      }

      const data = await response.json();

      // Non-token responses: a phone is already connected, the round is finished,
      // or a rotation was throttled — surface the state, don't render a stale QR.
      if (data.already_paired) {
        setQrCode({ alreadyPaired: true });
        return;
      }
      if (data.round_completed) {
        setQrCode({ roundCompleted: true });
        return;
      }
      if (data.throttled) {
        setQrThrottled(true);
        setQrError("Please wait a moment before regenerating the QR code.");
        return;
      }

      const qrUrl = `${window.location.origin}/secondary-camera/${data.token}`;
      const qrCodeDataUrl = await QRCode.toDataURL(qrUrl, QR_CODE_OPTIONS);
      setQrCode({ success: true, token: data.token, qr_code: qrCodeDataUrl, url: qrUrl });
      setQrError(null);
    } catch (error) {
      let userMessage = "Failed to generate QR code. ";
      if (error.message.includes("not found")) {
        userMessage += "Interview session not found. Please refresh the page.";
      } else if (error.message.includes("Failed to fetch") || error.message.includes("Network")) {
        userMessage += "Network error. Please check your connection and try again.";
      } else {
        userMessage += "Please try again or contact support if the issue persists.";
      }
      setQrError(userMessage);
    } finally {
      setQrLoading(false);
    }
  };

  const checkPairingStatus = () =>
    isSecondaryCameraEnabled ? checkSecondaryCameraLive(interviewId, round) : Promise.resolve(false);

  return {
    qrCode,
    qrLoading,
    qrError,
    qrThrottled,
    generateSecondaryQR,
    checkPairingStatus,
  };
};
